<template>
  <UModal
    v-if="isOpen"
    v-model:open="isOpen"
    class="w-full sm:max-w-[38rem]"
    :title="t('workflow.approval.title')"
    :description="t('workflow.approval.description')"
    :ui="{      
      overlay: 'bg-gray-950/75 dark:bg-gray-950/90',
      header: 'flex items-center justify-between p-3 sm:px-3 min-h-12 bg-[#e9edf3] dark:bg-(--ui-bg-elevated)',
      body: 'p-1 sm:p-1',
      footer: 'flex items-center justify-between gap-1.5 p-4 sm:px-6'
    }"
  >
    <template #header>
      <VisuallyHidden>
        <DialogTitle>{{ t('workflow.approval.title') }}</DialogTitle>
        <DialogDescription>{{ t('workflow.approval.description') }}</DialogDescription>
      </VisuallyHidden>
      
      <div class="flex items-center justify-between w-full">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-semibold leading-6 text-(--ui-text)">
            {{ t('workflow.approval.title') }}
          </h3>
          <div class="flex items-center gap-2 text-xs text-(--ui-text-muted)">
            <span>{{ t('workflow.approval.workflowId', 'Workflow ID') }}:</span>
            <code class="font-mono font-medium text-(--ui-text)">{{ workflowId }}</code>
          </div>
        </div>
        
        <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark"
          class="ml-2 cursor-pointer"
          tabindex="-1"
          @click="isOpen = false"
        />
      </div>
    </template>
    
    <template #body>
      <div class="p-4 space-y-4">
        <div v-if="loading" class="py-10 flex flex-col items-center gap-2 text-(--ui-text-muted)">
          <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin" />
          <span>{{ t('workflow.approval.loading') }}</span>
        </div>
        
        <template v-else-if="editableSteps.length">
          <div class="space-y-3">
            <div class="flex justify-end">
              <UButton
                color="primary"
                variant="soft"
                size="xs"
                icon="i-heroicons-plus"
                @click="addStep"
              >
                {{ t('workflow.approval.addStep') }}
              </UButton>
            </div>
            <div
              v-for="(step, index) in editableSteps"
              :key="index"
              class="border border-(--ui-border) rounded-lg p-3 bg-(--ui-bg-elevated)/40 space-y-2"
            >
              <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <USelect
                  :model-value="getStepTypeValue(step)"
                  size="md"
                  class="w-52 cursor-pointer"
                  :ui="{ content: 'min-w-fit' }"
                  :items="stepTypeOptions"
                  :color="getStepTypeValue(step) ? 'neutral' : 'error'"
                  :placeholder="t('workflow.approval.stepTypePlaceholder')"
                  @update:model-value="value => onStepTypeChange(index, value)"
                />
                <div class="flex items-center gap-2">
                  <div class="flex gap-1">
                    <UButton
                      size="xs"
                      color="secondary"
                      variant="subtle"
                      icon="i-heroicons-arrow-up"
                      :disabled="index === 0"
                      @click="moveStep(index, -1)"
                    />
                    <UButton
                      size="xs"
                      color="secondary"
                      variant="subtle"
                      icon="i-heroicons-arrow-down"
                      :disabled="index === editableSteps.length - 1"
                      @click="moveStep(index, 1)"
                    />
                  </div>
                  <UButton
                    size="xs"
                    color="error"
                    variant="subtle"
                    icon="i-heroicons-trash"
                    @click="removeStep(index)"
                  />
                  <UBadge variant="soft" color="primary">{{ t('workflow.approval.step') }} {{ index + 1 }}</UBadge>
                </div>
              </div>
              
              <div class="flex flex-col gap-2 pt-1.5 pl-2">
                <div v-if="usesSupervisorEmail(step)">
                  <div class="relative">
                    <UInput
                      :model-value="step.email ?? ''"
                      type="email"
                      color="neutral"
                      placeholder=""
                      size="sm"
                      class="w-full sm:w-74 h-8 text-sm"
                      :ui="{ base: 'peer' }"
                      @update:model-value="value => onSupervisorEmailChange(index, value)"
                    >
                      <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                        <span class="inline-flex bg-default px-1 rounded">{{ t('workflow.approval.supervisorEmail') }}</span>
                      </label>
                    </UInput>
                  </div>
                  <p v-if="!step.email" class="text-xs text-red-600 dark:text-red-400">
                    {{ t('workflow.approval.supervisorEmailRequired') }}
                  </p>
                </div>
                
                <div v-if="usesNotRegisteredUserEmail(step)">
                  <div class="relative">
                    <UInput
                      :model-value="step.email ?? ''"
                      type="email"
                      color="neutral"
                      placeholder=""
                      size="sm"
                      class="w-full sm:w-74 h-8 text-sm"
                      :ui="{ base: 'peer' }"
                      @update:model-value="value => onNotRegisteredUserEmailChange(index, value)"
                    >
                      <label class="pointer-events-none absolute left-0 -top-2.5 text-highlighted text-xs font-medium px-1.5 transition-all peer-focus:-top-2.5 peer-focus:text-highlighted peer-focus:text-xs peer-focus:font-medium peer-placeholder-shown:text-sm peer-placeholder-shown:text-dimmed peer-placeholder-shown:top-1.5 peer-placeholder-shown:font-medium">
                        <span class="inline-flex bg-default px-1 rounded">{{ t('workflow.approval.notRegisteredUserEmail') }}</span>
                      </label>
                    </UInput>
                  </div>
                  <p v-if="!step.email" class="text-xs text-red-600 dark:text-red-400">
                    {{ t('workflow.approval.notRegisteredUserEmailRequired') }}
                  </p>
                </div>
                
                <div v-if="usesPersonSelection(step)">
                  <USelect
                    v-if="clientEmployees.length"
                    :model-value="step.keycloakId ?? ''"
                    :items="clientEmployees"
                    size="sm"
                    color="primary"
                    :placeholder="t('workflow.approval.personPlaceholder')"
                    @update:model-value="value => onPersonChange(index, value)"
                  />
                  <p v-if="!clientEmployees.length && !loading" class="text-xs text-(--ui-text-muted)">
                    {{ t('workflow.approval.noEmployeesFound') }}
                  </p>
                  <p v-else-if="!step.keycloakId" class="text-xs text-red-600 dark:text-red-400">
                    {{ t('workflow.approval.personRequired') }}
                  </p>
                </div>
                
                <!--
                <div v-if="usesGroupSelection(step)">
                  <USelect
                    :model-value="step.groupPath ?? ''"
                    :items="[]"
                    size="sm"
                    color="primary"
                    :placeholder="t('workflow.approval.groupPlaceholder')"
                    @update:model-value="value => onGroupChange(index, value)"
                  />
                  <p v-if="!step.groupPath" class="text-xs text-red-600 dark:text-red-400">
                    {{ t('workflow.approval.groupRequired') }}
                  </p>
                </div>
                -->
              </div>
            </div>
            <p v-if="hasUnselectedStep" class="text-xs text-red-600 dark:text-red-400 text-right">
              {{ t('workflow.approval.stepTypeRequired') }}
            </p>
          </div>
        </template>
        
        <div v-else class="py-8 text-center text-sm text-(--ui-text-muted) space-y-4">
          <div>
            {{ error || t('workflow.approval.empty') }}
          </div>
          <UButton
            color="primary"
            variant="soft"
            size="sm"
            icon="i-heroicons-plus"
            @click="addStep"
          >
            {{ t('workflow.approval.addFirstStep') }}
          </UButton>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-wrap gap-2">
        <UButton
          color="primary"
          :loading="saving"
          :disabled="!canSave"
          @click="saveApprovalFlow"
        >
          <span class="sm:hidden">{{ t('workflow.approval.saveShort') }}</span>
          <span class="hidden sm:inline">{{ t('workflow.approval.save') }}</span>
        </UButton>
        <UButton color="neutral" variant="soft" @click="isOpen = false">
          {{ t('workflow.approval.close') }}
        </UButton>
      </div>
      <UButton
        v-if="hasClientId"
        color="primary"
        :loading="saving && savingClientFlow"
        :disabled="!canSaveWorkflowAndClient"
        @click="saveWorkflowAndClientApprovalFlow"
      >
        <span class="sm:hidden">{{ t('workflow.approval.saveWorkflowAndClientShort') }}</span>
        <span class="hidden sm:inline">{{ t('workflow.approval.saveWorkflowAndClient') }}</span>
      </UButton>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, ref, watch, resolveComponent } from 'vue'
import { useApiAdapter } from '@/store';
import { api, type ApprovalFlowDto } from '@/api/eOrdersApi.ts'
import { showError } from '@/utils/utils.ts'
import { useI18n } from 'vue-i18n'
import { DialogTitle, DialogDescription, VisuallyHidden } from 'reka-ui'

const UButton = resolveComponent('UButton')
const UBadge = resolveComponent('UBadge')
const UIcon = resolveComponent('UIcon')
const USelect = resolveComponent('USelect')
const UInput = resolveComponent('UInput')

const props = defineProps<{
  modelValue: boolean
  workflowId: string | null
  clientId?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'saved': []
}>()

const { t, locale } = useI18n()
const apiAdapter = useApiAdapter()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

type GenericApprovalStep = Record<string, any>

const approvalFlow = ref<ApprovalFlowDto | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const editableSteps = ref<GenericApprovalStep[]>([])
const saving = ref(false)
const savingClientFlow = ref(false)

const sortStepsByOrder = (steps: GenericApprovalStep[] = []) =>
  [...steps].sort((a, b) => {
    const getOrder = (step: GenericApprovalStep) => (typeof step?.order === 'number' ? step.order : 0)
    return getOrder(a) - getOrder(b)
  })

const syncEditableSteps = (steps: GenericApprovalStep[] = []) => {
  editableSteps.value = sortStepsByOrder(steps).map(step => ({ ...step }))
}

const createEmptyStep = (): GenericApprovalStep => ({
  order: editableSteps.value.length
})

const fetchApprovalFlow = async (id: string) => {
  loading.value = true
  error.value = null
  approvalFlow.value = null

  try {
    const result = await apiAdapter.invokeRaw<ApprovalFlowDto>(
      requestConfig => api.workflowQuotationGetApprovalFlow(id, requestConfig),
      (err: unknown) => {
        const message = err instanceof Error ? err.message : t('workflow.approval.error')
        showError(message)
        error.value = message
      }
    )

    if (result?.type === 'success') {
      const data = result.data
      
      if (data) {
        approvalFlow.value = data
        syncEditableSteps(data.steps || [])
      } else {
        approvalFlow.value = null
        editableSteps.value = []
      }
    } else {
      editableSteps.value = []
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : t('workflow.approval.error')
    showError(message)
    error.value = message
    editableSteps.value = []
  } finally {
    loading.value = false
  }
}

const availableStepTypes = [
  'DriverApprovalStep',
  'HrApprovalStep',
  'FleetManagerApprovalStep',
  'PurchaseSupportApprovalStep',
  'SupervisorApprovalStep',
  'NotRegisteredUserApprovalStep',
  //'GroupApprovalStep',
  'PersonApprovalStep'
] as const;

const clientEmployees = ref<{ label: string; value: string }[]>([])

const fetchClientEmployees = async (clientId: string) => {
  const result = await apiAdapter.invokeRaw<{ keycloakId: string; fullName: string }[]>(
    requestConfig => api.getClientRegisteredEmployees(clientId, requestConfig),
    (err: unknown) => {
      const message = err instanceof Error ? err.message : t('workflow.approval.error')
      showError(message)
    }
  )
  
  if (result?.type === 'success' && result.data) {
    clientEmployees.value = result.data.map(user => ({
      label: user.fullName,
      value: user.keycloakId
    }))
  } else {
    clientEmployees.value = []
  }
}

const stepTypeOptions = computed(() =>
  availableStepTypes.map(typeName => ({
    label: t(`workflow.approval.stepTypes.${typeName[0].toLowerCase()}${typeName.slice(1)}`),
    value: typeName
  }))
);

const getStepTypeValue = (step: GenericApprovalStep) => step?.$type?.split('.')?.pop() || step?.type || ''

const usesSupervisorEmail = (step: GenericApprovalStep) => getStepTypeValue(step) === 'SupervisorApprovalStep'
const usesNotRegisteredUserEmail = (step: GenericApprovalStep) => getStepTypeValue(step) === 'NotRegisteredUserApprovalStep'
const usesPersonSelection = (step: GenericApprovalStep) => getStepTypeValue(step) === 'PersonApprovalStep'
//const usesGroupSelection = (step: GenericApprovalStep) => getStepTypeValue(step) === 'GroupApprovalStep'

const hasUnselectedStep = computed(() =>
  editableSteps.value.some(step => !getStepTypeValue(step))
)

const hasMissingPersonSelection = computed(() =>
  editableSteps.value.some(
    step => usesPersonSelection(step) && !step.keycloakId
  )
)

/*
const hasMissingGroupSelection = computed(() =>
  editableSteps.value.some(step => usesGroupSelection(step) && !step.groupPath)
)
*/

const hasMissingSupervisorEmail = computed(() =>
  editableSteps.value.some(step => usesSupervisorEmail(step) && !step.email)
)

const hasMissingNotRegisteredUserEmail = computed(() =>
  editableSteps.value.some(step => usesNotRegisteredUserEmail(step) && !step.email)
)

const hasClientId = computed(
  () => typeof props.clientId === 'string' && props.clientId.length > 0
)

const onStepTypeChange = (index: number, value: string | null) => {
  const nextSteps = [...editableSteps.value]
  const step = { ...(nextSteps[index] || {}) }
  
  if (value) {
    step.$type = value
    step.type = value
  } else {
    delete step.$type
    delete step.type
  }

  if (value !== 'SupervisorApprovalStep' && value !== 'NotRegisteredUserApprovalStep') {
    delete step.email
  }

  if (value !== 'PersonApprovalStep') {
    delete step.keycloakId
  }
  
  /*
  if (value !== 'GroupApprovalStep') {
    delete step.groupPath
  }
  */
  
  nextSteps[index] = step
  editableSteps.value = nextSteps
}

const onSupervisorEmailChange = (index: number, value: string) => {
  const nextSteps = [...editableSteps.value]
  const step = { ...(nextSteps[index] || {}) }
  
  step.email = value?.trim() || undefined
  nextSteps[index] = step
  editableSteps.value = nextSteps
}

const onNotRegisteredUserEmailChange = (index: number, value: string) => {
  const nextSteps = [...editableSteps.value]
  const step = { ...(nextSteps[index] || {}) }
  
  step.email = value?.trim() || undefined
  nextSteps[index] = step
  editableSteps.value = nextSteps
}

const onPersonChange = (index: number, value: string) => {
  const nextSteps = [...editableSteps.value]
  const step = { ...(nextSteps[index] || {}) }
  
  step.keycloakId = value || undefined
  nextSteps[index] = step
  editableSteps.value = nextSteps
}

/*
const onGroupChange = (index: number, value: string) => {
  const nextSteps = [...editableSteps.value]
  const step = { ...(nextSteps[index] || {}) }
  
  step.groupPath = value || undefined
  nextSteps[index] = step
  editableSteps.value = nextSteps
}
*/

const updateStepOrders = () => {
  editableSteps.value = editableSteps.value.map((step, index) => ({
    ...step,
    order: index
  }))
}

const moveStep = (index: number, direction: -1 | 1) => {
  const targetIndex = index + direction
  
  if (targetIndex < 0 || targetIndex >= editableSteps.value.length) {
    return
  }

  const nextSteps = [...editableSteps.value]
  const [moved] = nextSteps.splice(index, 1)
  
  nextSteps.splice(targetIndex, 0, moved)
  editableSteps.value = nextSteps
  
  updateStepOrders()
}

const addStep = () => {
  editableSteps.value = [...editableSteps.value, createEmptyStep()]
  
  updateStepOrders()
}

const removeStep = (index: number) => {
  const nextSteps = [...editableSteps.value]
  
  nextSteps.splice(index, 1)
  editableSteps.value = nextSteps
  
  updateStepOrders()
}

const buildStepPayload = (steps: GenericApprovalStep[]) =>
  steps.map((step, index) => {
    const orderValue = typeof step.order === 'number' ? step.order : index
    const typeValue = step.$type || step.type
    const payload: Record<string, any> = {}
    
    if (typeValue) {
      payload.$type = typeValue
    }
    
    payload.order = orderValue
    
    if ((typeValue === 'SupervisorApprovalStep' || typeValue === 'NotRegisteredUserApprovalStep') && step.email) {
      payload.email = step.email
    }

    if (typeValue === 'PersonApprovalStep' && step.keycloakId) {
      payload.keycloakId = step.keycloakId
    }

    /*
    if (typeValue === 'GroupApprovalStep' && step.groupPath) {
      payload.groupPath = step.groupPath
    }
    */

    return payload
  })

const hasChanges = computed(() => {
  const originalSteps = approvalFlow.value?.steps ?? []
  const currentSteps = editableSteps.value
  const original = JSON.stringify(sortStepsByOrder(originalSteps))
  const current = JSON.stringify(sortStepsByOrder(currentSteps))
  
  return original !== current
})

const canSave = computed(
  () =>
    !loading.value &&
    !saving.value &&
    hasChanges.value &&
    !hasUnselectedStep.value &&
    !hasMissingPersonSelection.value &&
    //!hasMissingGroupSelection.value &&
    !hasMissingSupervisorEmail.value &&
    !hasMissingNotRegisteredUserEmail.value
)

const canSaveWorkflowAndClient = computed(
  () =>
    hasClientId.value &&
    canSave.value &&
    !saving.value &&
    !savingClientFlow.value
)

const persistWorkflowApprovalFlow = async () => {
  await apiAdapter.invoke<void>(
    requestConfig =>
      api.workflowQuotationUpdateApprovalFlow(
        props.workflowId as string,
        { steps: buildStepPayload(editableSteps.value) },
        {
          ...requestConfig,
          successNotificationMessage: t('workflow.approval.saveSuccess')
        }
      ),
    (err: unknown) => {
      const message = err instanceof Error ? err.message : t('workflow.approval.saveError')
      showError(message)
    }
  )

  approvalFlow.value = approvalFlow.value
    ? { ...approvalFlow.value, steps: editableSteps.value.map(step => ({ ...step })) }
    : {
        steps: editableSteps.value.map(step => ({ ...step }))
      }
}

const persistClientApprovalFlow = async () => {
  await apiAdapter.invoke<void>(
    requestConfig =>
      api.updateApprovalFlow(
        props.clientId as string,
        { steps: buildStepPayload(editableSteps.value) },
        {
          ...requestConfig,
          successNotificationMessage: t('workflow.approval.clientSaveSuccess')
        }
      ),
    (err: unknown) => {
      const message = err instanceof Error ? err.message : t('workflow.approval.clientSaveError')
      showError(message)
    }
  )
}

const saveApprovalFlow = async () => {
  if (!props.workflowId) {
    return
  }

  saving.value = true
  
  try {
    await persistWorkflowApprovalFlow()
    emit('saved')
  } finally {
    saving.value = false
  }
}

const saveWorkflowAndClientApprovalFlow = async () => {
  if (!props.workflowId || !props.clientId) {
    return
  }

  saving.value = true
  savingClientFlow.value = true

  try {
    await persistWorkflowApprovalFlow()
    await persistClientApprovalFlow()
    emit('saved')
  } finally {
    saving.value = false
    savingClientFlow.value = false
  }
}

watch(
  () => [isOpen.value, props.workflowId],
  ([open, id]) => {
    if (open && typeof id === 'string' && id.length > 0) {
      fetchApprovalFlow(id)
      if (props.clientId) {
        fetchClientEmployees(props.clientId)
      }
    } else if (!open) {
      approvalFlow.value = null
      error.value = null
      editableSteps.value = []
      clientEmployees.value = []
    }
  },
  { immediate: true }
)

watch(() => approvalFlow.value?.steps, steps => {
  if (steps) {
    syncEditableSteps(steps as GenericApprovalStep[])
  }
})
</script>